import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
// ============================================================
// ADMIN: Add or remove photos by editing this array.
// Each entry needs a `src` (full image URL) and `alt` description.
// ============================================================
const galleryPhotos = [{
  src: 'https://cdn.magicpatterns.com/uploads/tHx2awfeErZsyTBv2bEsWN/Before_and_After_Gallery_Site.jpg',
  alt: 'Before and after exterior detail - Honda SUV transformation'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/r3K5F3iBTX3mJJMgBHYwcy/before_and_after.jpg',
  alt: 'Before and after interior detail - deep clean cup holders and floor'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/jnMLPWWUnotWKwDNmBg1do/red_truck.jpg',
  alt: 'Red Ford Raptor full detail - showroom shine exterior'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/8mQmX2nHGwDLJzZM9TjJJH/water_spots.jpg',
  alt: 'Before and after water spot removal - exterior detail'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/ned8mdPgcS8nYQ5VpmCn7T/Red_BMW_.jpg',
  alt: 'Red BMW M4 convertible full detail - showroom shine'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6eB2pXEEasWqzkcY2DJRjT/engine_cleaning_.jpg',
  alt: 'Engine bay cleaning - 5.7L V8 HEMI spotless engine detail'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nJVDHP7sdWCxr5NneiPVLe/old_mustang.jpg',
  alt: 'Classic blue Ford Mustang 289 full detail - mirror finish exterior detail'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/bhxv8y6J5VzjsqtsvD6ADU/Old_Porshe.jpg',
  alt: 'Vintage black Porsche 356 Speedster detail - showroom quality exterior and red interior'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/dw73qFfeQfqmLiuAy4XWKW/Old_Red_Camaro.jpg',
  alt: 'Classic red Camaro SS 350 full detail - chrome and paint restoration'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fhqQQbYfTwbAoA2HAQTAZq/porshe_1956.jpg',
  alt: 'Vintage black Porsche 356 Speedster detail - pristine exterior with red leather interior'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/kgpCy88yxExYomBniBTzVn/White_Mustang.jpg',
  alt: 'Classic white Ford Mustang with blue racing stripes - full exterior detail'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/4sUvi6gzZAaQfENg49ctLt/Classic.jpg',
  alt: 'Classic blue and white Chevrolet Corvette convertible full detail - chrome and whitewall tires'
}];
export function Gallery() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const goNext = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex + 1) % galleryPhotos.length);
    }
  };
  const goPrev = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex - 1 + galleryPhotos.length) % galleryPhotos.length);
    }
  };
  return <section className="w-full bg-black py-20 px-4 border-t border-zinc-900" id="gallery" data-id="element-188" data-mp="12wvgjhk1k06n">
      <div className="max-w-6xl mx-auto" data-id="element-189" data-mp="17pf1x0oyzzm0">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-190" data-mp="tx8q1qsuoesd">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase mb-4" data-id="element-191" data-mp="tyjrvl2qjcgu">
            <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500" data-id="element-192" data-mp="xzlqbzotvkhr">
              Our Work — Before & After Detailing Results
            </span>
          </h2>
          <div className="h-1 w-24 bg-red-600 mx-auto rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)]" data-id="element-193" data-mp="1qmpr1sgw1cnq" />
          <p className="text-gray-400 text-lg mt-6" data-id="element-194" data-mp="1cn6w61d37xtd">
            See the difference professional mobile car detailing makes — real
            results from real customers in Ventura County
          </p>
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-195" data-mp="674nznvb8n3h">
          {galleryPhotos.map((photo, index) => <motion.div key={photo.src} initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="rounded-xl overflow-hidden cursor-pointer group border border-zinc-800 hover:border-zinc-700 transition-colors" onClick={() => openLightbox(index)} data-id="element-196" data-mp="lhkoo4wtrj4g" data-mpi={index}>
              <img src={photo.src} alt={photo.alt} className="w-full max-w-[1200px] h-auto object-contain bg-zinc-950 transition-transform duration-500 group-hover:scale-[1.02] mx-auto" loading="lazy" decoding="async" data-id="element-197" data-mp="14dsy7kwveg5k" />
            </motion.div>)}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-198" data-mp="qi8h8v0pc80s">
        {lightboxIndex !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={closeLightbox} data-id="element-199" data-mp="3g5pxqtruquav">
            <button onClick={closeLightbox} className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10" aria-label="Close lightbox" data-id="element-200" data-mp="1qq5e8qhfj86k">
              <XIcon className="w-8 h-8" data-id="element-201" data-mp="1zoh8d2hi8o3r" />
            </button>

            <button onClick={e => {
          e.stopPropagation();
          goPrev();
        }} className="absolute left-4 text-white/70 hover:text-white p-2 z-10" aria-label="Previous photo" data-id="element-202" data-mp="1zn5zwpd46s6b">
              <ChevronLeftIcon className="w-10 h-10" data-id="element-203" data-mp="29h6dzgqdj7r6" />
            </button>

            <button onClick={e => {
          e.stopPropagation();
          goNext();
        }} className="absolute right-4 text-white/70 hover:text-white p-2 z-10" aria-label="Next photo" data-id="element-204" data-mp="2ecc1gjzfcuhd">
              <ChevronRightIcon className="w-10 h-10" data-id="element-205" data-mp="2ne1prgy0fdax" />
            </button>

            <motion.img key={galleryPhotos[lightboxIndex].src} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} transition={{
          duration: 0.2
        }} src={galleryPhotos[lightboxIndex].src} alt={galleryPhotos[lightboxIndex].alt} className="max-w-full max-h-[85vh] object-contain rounded-lg" style={{
          maxWidth: '1200px'
        }} onClick={e => e.stopPropagation()} data-id="element-206" data-mp="p00e1fv0en5w" />

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center" data-id="element-207" data-mp="2199dlae7vaqx">
              <span className="text-white/60 text-sm" data-id="element-208" data-mp="2f90vvnbrndaq">
                {lightboxIndex + 1} / {galleryPhotos.length}
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}